import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Phone, Globe } from "lucide-react";
import { useBookingModal } from "../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";
import { LanguageLink } from "./LanguageLink";
import {
  parseLanguageFromPath,
  buildLanguageUrl,
  getBasePath,
  SupportedLanguage,
} from "../utils/languageRoutes";

export const Navigation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileToursOpen, setMobileToursOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { openModal } = useBookingModal();

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "nl", name: "Nederlands", flag: "🇳🇱" },
  ];

  const changeLanguage = (languageCode: SupportedLanguage) => {
    const { path } = parseLanguageFromPath(location.pathname);
    const basePath = getBasePath(path, i18n.language as SupportedLanguage);
    const newUrl = buildLanguageUrl(basePath, languageCode);

    i18n.changeLanguage(languageCode);
    navigate(newUrl);
    setActiveDropdown(null);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const tours = [
    "Bruges",
    "Giethoorn",
    "Keukenhof",
    "Marken",
    "Volendam",
    "Zaanse Schans",
    "Brussels",
    "Antwerp",
  ];

  const translateTour = (tour: string) => {
    const tourTranslations: { [key: string]: string } = {
      Bruges: t("1062", "Bruges"),
      Giethoorn: t("1063", "Giethoorn"),
      Keukenhof: t("1064", "Keukenhof"),
      Marken: t("1065", "Marken"),
      Volendam: t("1066", "Volendam"),
      "Zaanse Schans": t("1067", "Zaanse Schans"),
      Brussels: t("1068", "Brussels"),
      Antwerp: t("1069", "Antwerp"),
    };
    return tourTranslations[tour] || tour;
  };

  const isActive = (path: string) => {
    const { path: currentPath } = parseLanguageFromPath(location.pathname);
    return currentPath === path || (path === "/" && currentPath === "");
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-gray-700"
          : "bg-gray-900/80 backdrop-blur-xs"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <LanguageLink to="/" className="flex items-center group">
            <motion.img
              src="/images/logo-2.png"
              alt={t("1081", "TaxiSchipholAirport")}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className="h-16 w-auto"
            />
          </LanguageLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {[
              { path: "/", label: t("1070", "Home") },
              { path: "/about", label: t("1071", "About Us") },
              { path: "/services", label: t("1072", "Services") },
              { path: "/booking", label: t("1073", "Booking") },
            ].map((item) => (
              <LanguageLink
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive(item.path)
                    ? scrolled
                      ? "text-primary bg-primary/20"
                      : "text-primary bg-primary/20"
                    : scrolled
                    ? "text-gray-200 hover:text-primary hover:bg-gray-800"
                    : "text-white hover:text-primary hover:bg-white/10"
                }`}
              >
                {item.label}
                {isActive(item.path) && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-linear-to-r from-primary to-primary-light rounded-full"
                  />
                )}
              </LanguageLink>
            ))}

            {/* Tours Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown("tours")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                  scrolled
                    ? "text-gray-200 hover:text-primary hover:bg-gray-800"
                    : "text-white hover:text-warning hover:bg-white/10"
                }`}
              >
                {t("1074", "Tours")}
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <AnimatePresence>
                {activeDropdown === "tours" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-600 overflow-hidden"
                  >
                    {tours.map((tour) => {
                      const tourRoutes: { [key: string]: string } = {
                        Giethoorn: "/tours/giethoorn",
                        Bruges: "/tours/bruges",
                        Keukenhof: "/tours/keukenhof",
                        Marken: "/tours/marken",
                        Volendam: "/tours/volendam",
                        "Zaanse Schans": "/tours/zaanse-schans",
                        Brussels: "/tours/brussels",
                        Antwerp: "/tours/antwerp",
                      };

                      return (
                        <motion.div
                          key={tour}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                        >
                          <LanguageLink
                            to={tourRoutes[tour]}
                            className="block px-4 py-3 text-sm text-gray-200 hover:bg-primary/20 hover:text-primary transition-colors first:rounded-t-2xl last:rounded-b-2xl"
                          >
                            {translateTour(tour)}
                          </LanguageLink>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <LanguageLink
              to="/city-transfers"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                isActive("/city-transfers")
                  ? scrolled
                    ? "text-primary bg-primary/20"
                    : "text-primary bg-primary/20"
                  : scrolled
                  ? "text-gray-200 hover:text-primary hover:bg-gray-800"
                  : "text-white hover:text-warning hover:bg-white/10"
              }`}
            >
              {t("1075", "City Transfers")}
            </LanguageLink>

            <LanguageLink
              to="/contact"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                isActive("/contact")
                  ? scrolled
                    ? "text-primary bg-primary/20"
                    : "text-primary bg-primary/20"
                  : scrolled
                  ? "text-gray-200 hover:text-primary hover:bg-gray-800"
                  : "text-white hover:text-warning hover:bg-white/10"
              }`}
            >
              {t("1076", "Contact")}
            </LanguageLink>

            {/* Language Selector */}
            <div
              className="relative ml-4"
              onMouseEnter={() => setActiveDropdown("language")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                  scrolled
                    ? "text-gray-200 hover:text-primary hover:bg-gray-800"
                    : "text-white hover:text-primary hover:bg-white/10"
                }`}
              >
                <Globe className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline mr-1">
                  {languages.find((lang) => lang.code === i18n.language)
                    ?.flag || "🇬🇧"}
                </span>
                <span className="hidden md:inline">
                  {i18n.language.toUpperCase()}
                </span>
                <ChevronDown className="ml-1 h-3 w-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === "language" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-40 bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-600 overflow-hidden"
                  >
                    {languages.map((language) => (
                      <motion.button
                        key={language.code}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        onClick={() =>
                          changeLanguage(language.code as SupportedLanguage)
                        }
                        className={`w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors first:rounded-t-2xl last:rounded-b-2xl ${
                          i18n.language === language.code
                            ? "bg-primary/20 text-primary"
                            : "text-gray-200 hover:bg-primary/20 hover:text-primary"
                        }`}
                      >
                        <span className="text-base">{language.flag}</span>
                        <span className="font-medium">{language.name}</span>
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center space-x-3 ml-2">
              <a
                href="tel:+31639182624"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  scrolled
                    ? "text-gray-200 hover:text-primary hover:bg-gray-800"
                    : "text-white hover:text-warning hover:bg-white/10"
                }`}
              >
                <Phone className="h-4 w-4" />
                <span className="hidden xl:inline">
                  {t("1077", "Call Now")}
                </span>
              </a>

              <button
                onClick={openModal}
                className="group relative overflow-hidden bg-linear-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <motion.div
                  className="absolute inset-0 bg-white/20"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.6 }}
                />
                <span className="relative z-10">{t("1078", "Book Now")}</span>
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            className={`lg:hidden p-2 rounded-xl transition-colors ${
              scrolled
                ? "text-gray-200 hover:bg-gray-800"
                : "text-white hover:bg-white/10"
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={
              isMenuOpen
                ? t("1079", "Close navigation menu")
                : t("1080", "Open navigation menu")
            }
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </motion.button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-gray-900/95 backdrop-blur-md border-t border-gray-700"
          >
            <div className="px-4 py-6 space-y-2">
              {[
                { path: "/", label: t("1070", "Home") },
                { path: "/about", label: t("1071", "About Us") },
                { path: "/services", label: t("1072", "Services") },
                { path: "/booking", label: t("1073", "Booking") },
                { path: "/contact", label: t("1076", "Contact") },
              ].map((item) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <LanguageLink
                    to={item.path}
                    className="block px-4 py-3 text-base font-medium text-gray-200 hover:text-primary hover:bg-primary/20 rounded-xl transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </LanguageLink>
                </motion.div>
              ))}

              {/* Tours Dropdown */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <button
                  onClick={() => setMobileToursOpen(!mobileToursOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-base font-medium text-gray-200 hover:text-primary hover:bg-primary/20 rounded-xl transition-colors"
                >
                  {t("1074", "Tours")}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      mobileToursOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {mobileToursOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="ml-4 mt-2 space-y-1"
                    >
                      {tours.map((tour) => {
                        const tourRoutes: { [key: string]: string } = {
                          Giethoorn: "/tours/giethoorn",
                          Bruges: "/tours/bruges",
                          Keukenhof: "/tours/keukenhof",
                          Marken: "/tours/marken",
                          Volendam: "/tours/volendam",
                          "Zaanse Schans": "/tours/zaanse-schans",
                          Brussels: "/tours/brussels",
                          Antwerp: "/tours/antwerp",
                        };

                        return (
                          <LanguageLink
                            key={tour}
                            to={tourRoutes[tour]}
                            className="block px-4 py-2 text-sm font-medium text-gray-300 hover:text-primary hover:bg-primary/20 rounded-lg transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {translateTour(tour)}
                          </LanguageLink>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* City Transfers */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <LanguageLink
                  to="/city-transfers"
                  className="block px-4 py-3 text-base font-medium text-gray-200 hover:text-primary hover:bg-primary/20 rounded-xl transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("1075", "City Transfers")}
                </LanguageLink>
              </motion.div>

              {/* Language Selector Mobile */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="px-4 py-2">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    {t("1082", "Language")}
                  </div>
                  <div className="flex space-x-2">
                    {languages.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => {
                          changeLanguage(language.code as SupportedLanguage);
                          setIsMenuOpen(false);
                        }}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          i18n.language === language.code
                            ? "bg-primary/20 text-primary border border-primary/30"
                            : "bg-gray-800/50 text-gray-200 hover:bg-primary/20 hover:text-primary border border-gray-600"
                        }`}
                      >
                        <span className="text-base">{language.flag}</span>
                        <span className="font-medium">
                          {language.code.toUpperCase()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Call Now */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <a
                  href="tel:+31639182624"
                  className="flex items-center space-x-2 px-4 py-3 text-base font-medium text-gray-200 hover:text-primary hover:bg-primary/20 rounded-xl transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  <span>{t("1077", "Call Now")}</span>
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="pt-4 border-t border-gray-700"
              >
                <button
                  onClick={() => {
                    openModal();
                    setIsMenuOpen(false);
                  }}
                  className="block bg-linear-to-r from-primary to-primary-light text-white px-4 py-3 rounded-xl text-base font-semibold text-center w-full"
                >
                  {t("1078", "Book Now")}
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
